/* eslint-disable @typescript-eslint/no-explicit-any */

import { useApiV10EnrollableBenefitsPricingFormulasTestCreate } from '@ben/api/gen/codegen';
import RiQuestionLine from '@ben/icons/question-line';
import { CURRENCY_OPTIONS_WITH_SYMBOL } from '@ben/orion/utils/currencies';
import Button from '@ben/orion/button';
import Checkbox from '@ben/orion/checkbox';
import type { ISelectOption } from '@ben/orion/deprecated-select';
import Select from '@ben/orion/deprecated-select';
import InlineTextButton from '@ben/orion/inline-text-button';
import Input, { InputVariant } from '@ben/orion/input';
import RadioButton from '@ben/orion/radio-button';
import { RIcon } from '@ben/orion/remix-icon/remix-icon';
import Tooltip from '@ben/orion/tooltip';
import Typography, { TypographyVariant } from '@ben/orion/typography';
import {
  DefaultDeductionSourceEnum,
  PaymentSourceEnum,
} from '@ben/shared/types/benefit';
import {
  type IModularBenefitDetail,
  type IModularBenefitMessages,
  type ISubPrice,
} from '@ben/shared/types/columbia';
import { exportCsvFile } from '@ben/shared/utils/export-csv-file';
import { memo, useEffect } from 'react';
import type { Control } from 'react-hook-form';
import {
  Controller,
  useFieldArray,
  useFormContext,
  useWatch,
} from 'react-hook-form';
import { toast } from 'react-toastify';
import styled, { css, useTheme } from 'styled-components';
import AIToolWrapper from '../../assistant/ai-tool-wrapper';
import { fieldCannotBeBlankErrorMessage } from '../../shared/constants';
import { generateNewRank } from '../../utils';
import type { RateTableFields, StepsFields } from '../elements/types';
import DynamicRecalculationConfig from './dynamic-recalculation';
import FormulaHelpChips from './formula-help-chips';

const paymentSources = [
  {
    label: 'Salary',
    enum: PaymentSourceEnum.SALARY,
    tooltip: "Cost deducted from employee's salary only, no fallback.",
  },
  {
    label: 'Allowance',
    enum: PaymentSourceEnum.ALLOWANCE,
    tooltip: 'Cost deducted from allowance balance only, no fallback.',
  },
  {
    label: 'Salary and Allowance',
    enum: PaymentSourceEnum.SALARY_ALLOWANCE,
    tooltip:
      'Employee can choose to split cost between salary and allowance, any remainder falls back to salary. Once a remainder falls back to salary, it will not switch back to allowance.',
  },
  {
    label: 'Salary or Allowance',
    enum: PaymentSourceEnum.SALARY_OR_ALLOWANCE,
    tooltip:
      'Employee can choose for the full amount to be paid from one source (salary or allowance), no splitting between both is allowed. If allowance is chosen then cannot cover full amount it will fallback to salary. Once a remainder falls back to salary, it will not switch back to allowance.',
  },
  {
    label: 'Allowance then Salary',
    enum: PaymentSourceEnum.ALLOWANCE_THEN_SALARY,
    tooltip:
      'Deduct maximum possible from allowance first, remaining balance deducted from salary, employees cannot override or manually edit splits. Automatically reverts to allowance usage when unallocated allowance becomes available. This is currently only supported for MONTHLY allowance renewal frequency.',
  },
];

const ageCalculationOptions = [
  { key: 'TODAY', label: 'Current age' },
  {
    key: 'OPT_IN_DATE',
    label: 'Age when opted in',
  },
  {
    key: 'ENROLMENT_START',
    label: 'Age at benefit activation',
  },
  {
    key: 'POLICY_LAUNCH',
    label: 'Age at policy launch',
  },
  {
    key: 'NEXT_BIRTHDAY',
    label: 'Age at next birthday',
  },
  {
    key: 'EMPLOYEE_POLICY',
    label: "Age at Employee's policy start date",
  },
  {
    key: 'MAX_AGE_CURRENT_YEAR',
    label: 'Max age in the current year',
  },
  {
    key: 'NEXT_RENEWAL',
    label: 'Age at next renewal',
  },
];

const salaryCalculationOptions = [
  { key: 'TODAY', label: 'Current date (of price calculation)' },
  { key: 'POLICY_START_DATE', label: 'Policy start date' },
  { key: 'ENROLMENT_START_DATE', label: 'Enrolment start date' },
];

const rateTableCalculateFromOptions = [
  {
    key: 'RENEWAL_OR_POLICY_START_DATE',
    label: 'Renewal or policy start date',
  },
  { key: 'POLICY_START_DATE', label: 'Policy start date' },
];

const emptyString = undefined;

interface PricesProps {
  errors?: any;
  warnings: IModularBenefitMessages;
  successes: IModularBenefitMessages;
  stepsControl?: Control<StepsFields>;
  rateTableControl?: Control<RateTableFields>;
}

const Prices = ({
  errors,
  warnings,
  successes,
  stepsControl,
  rateTableControl,
}: PricesProps) => {
  const theme = useTheme();

  const { mutate: testPricingFormulaMutation, isLoading: isLoadingTest } =
    useApiV10EnrollableBenefitsPricingFormulasTestCreate({
      mutation: {
        onSuccess: (data) => {
          exportCsvFile({
            data,
            fileName: 'changes-and-errors-pricing-formulas',
          });
          setSubPricesNeedTesting(false);
        },
        onError: () => {
          toast.error('Failed to test pricing formulas');
          setSubPricesNeedTesting(false);
        },
      },
    });

  const { control, setValue, getValues } =
    useFormContext<IModularBenefitDetail>();

  const { fields, append, remove } = useFieldArray({
    name: 'additionalConfig.subPrices',
    control,
  });

  const setSubPricesNeedTesting = (value: boolean) => {
    setValue('additionalConfig.subPricesNeedTesting', value);
  };

  const allowEmployeePayDeductionChoice = useWatch({
    control,
    name: 'allowEmployeePayDeductionChoice',
    exact: true,
  });

  const remainderAttributionOptions: ISelectOption[] = [
    {
      key: 'FIRST_CYCLE',
      label: 'First cycle',
    },
    {
      key: 'LAST_CYCLE',
      label: 'Last cycle',
    },
  ];

  const addNewSubPrice = () => {
    if (fields.length === 0) setValue('additionalConfig.subPrices', []);
    const newRank = generateNewRank(fields) ?? fields.length;
    const newSubPrice = {
      id: undefined,
      rank: newRank,
      name: '',
      pricingFormula: '',
      employeeContribution: emptyString,
      maxEmployerContributionAmount: emptyString,
    } as ISubPrice;

    append(newSubPrice);
    setSubPricesNeedTesting(true);
  };

  const isPaymentSourceDisabled = !useWatch({
    control,
    name: 'showPrice',
    exact: true,
  });
  const allowanceImmediateDeduction = useWatch({
    control,
    name: 'allowanceImmediateDeduction',
    exact: true,
  });

  const paymentSource = useWatch({
    control,
    name: 'paymentSource',
    exact: true,
  });

  const isAllowanceThenSalary =
    paymentSource === PaymentSourceEnum.ALLOWANCE_THEN_SALARY;
  const hasMixedPaymentSource =
    paymentSource === PaymentSourceEnum.SALARY_ALLOWANCE ||
    paymentSource === PaymentSourceEnum.SALARY_OR_ALLOWANCE;

  // Reset allowanceImmediateDeduction when paymentSource is ALLOWANCE_THEN_SALARY
  // This handles both initial form load and when user changes the payment source
  useEffect(() => {
    if (isAllowanceThenSalary && allowanceImmediateDeduction) {
      setValue('allowanceImmediateDeduction', false);
    }
  }, [isAllowanceThenSalary, allowanceImmediateDeduction, setValue]);

  // Reset defaultDeductionSource when paymentSource is not a mixed source
  useEffect(() => {
    if (!hasMixedPaymentSource) {
      setValue(
        'defaultDeductionSource',
        DefaultDeductionSourceEnum.ALLOWANCE_FIRST,
        {
          shouldDirty: false,
        }
      );
    }
  }, [hasMixedPaymentSource, setValue]);

  const resetDeductionsAtRenewal = useWatch({
    control,
    name: 'resetDeductionsAtRenewal',
    exact: true,
  });

  useEffect(() => {
    if (!hasMixedPaymentSource && resetDeductionsAtRenewal) {
      // Mark the field dirty so the cleared value is included in the PATCH
      // payload (getDirtyValues only sends dirty fields). Without this, a
      // previously-persisted `true` would stay on the backend and fail the
      // server-side "mixed payment source" guard on the next save.
      setValue('resetDeductionsAtRenewal', false, { shouldDirty: true });
    }
  }, [hasMixedPaymentSource, resetDeductionsAtRenewal, setValue]);

  const handleTestPricingFormulas = () => {
    const subPrices = getValues('additionalConfig.subPrices');
    const payload = {
      benefitId: Number(getValues('id')),
      data: {
        subPrices: subPrices.map((subPrice: ISubPrice) => ({
          ...subPrice,
          employeeContribution: Number(subPrice.employeeContribution),
          maxEmployerContributionAmount: Number(
            subPrice.maxEmployerContributionAmount
          ).toString(),
        })),
      },
    };

    testPricingFormulaMutation(payload);
  };

  return (
    <Wrapper>
      <TitleWrapper>
        <Typography variant={TypographyVariant.h2}>Pricing</Typography>
        <Typography variant={TypographyVariant.bodyMd1}>
          All fields below are required, unless specified optional.
        </Typography>
      </TitleWrapper>
      <PricingConfigurationWrapper key="pricing-configuration">
        <ContentRow>
          <Controller
            name="currencyOverride"
            control={control}
            rules={{ required: fieldCannotBeBlankErrorMessage }}
            render={({
              field: { value, onChange, ref },
              fieldState: { error },
            }) => {
              return (
                <Select
                  key={value}
                  ref={ref}
                  name="Benefit currency"
                  defaultValue={value}
                  onChange={onChange}
                  options={CURRENCY_OPTIONS_WITH_SYMBOL}
                  hasError={!!error || !!errors?.currencyOverride}
                  errorMessage={fieldCannotBeBlankErrorMessage}
                  width="100%"
                  data-testid="benefit-currency-select"
                  showSearch
                />
              );
            }}
          />
        </ContentRow>
        <ContentRow>
          <Controller
            name="ageCalculateFrom"
            control={control}
            rules={{ required: fieldCannotBeBlankErrorMessage }}
            render={({ field, fieldState }) => {
              const { value, onChange, ref } = field;
              const { isDirty, error } = fieldState;
              return (
                <Select
                  key={value}
                  ref={ref}
                  name="Age calculation"
                  hintText="If employee’s age is required to calculate the price, specify when to calculate it from."
                  defaultValue={value}
                  onChange={onChange}
                  options={ageCalculationOptions}
                  hasError={!!error || !!errors?.ageCalculateFrom}
                  errorMessage={fieldCannotBeBlankErrorMessage}
                  warningMessage={
                    isDirty ? undefined : warnings.ageCalculateFrom
                  }
                  successMessage={
                    isDirty ? undefined : successes.ageCalculateFrom
                  }
                  width="100%"
                  data-testid="age-calculation-select"
                />
              );
            }}
          />
          <Controller
            name="remainderAttribution"
            control={control}
            rules={{ required: true }}
            render={({ field }) => {
              const { value, onChange, ...rest } = field;
              return (
                <Select
                  {...rest}
                  key={value?.toString()}
                  name="Remainder attribution"
                  hintText="Where should the remainder from calculation for a benefit be attributed?"
                  options={remainderAttributionOptions}
                  defaultValue={value?.toString()}
                  clearable
                  onChange={onChange}
                  data-testid="remainder-attribution-select"
                  width="100%"
                />
              );
            }}
          />
        </ContentRow>
        <ContentRow>
          <Controller
            name="allowEmployeePayDeductionChoice"
            control={control}
            rules={{ required: false }}
            render={({ field }) => {
              const { value, ...rest } = field;
              return (
                <PayDeductionTypeWrapper>
                  <Checkbox
                    {...rest}
                    label="Allow employee pay deduction choice"
                    showLabel
                    value={value || false}
                  />
                  <Tooltip tooltipText="Changing this on an enrolled benefit updates the pay deduction type on affected enrolments and their current subscriptions, which impacts payroll">
                    <RIcon
                      icon={RiQuestionLine}
                      color={theme.palette.neutral[80]}
                    />
                  </Tooltip>
                </PayDeductionTypeWrapper>
              );
            }}
          />
          <Controller
            name="payDeductionType"
            control={control}
            rules={{
              required: allowEmployeePayDeductionChoice
                ? fieldCannotBeBlankErrorMessage
                : false,
            }}
            render={({
              field: { value, onChange, ref },
              fieldState: { error },
            }) => {
              return (
                <Select
                  key={value}
                  ref={ref}
                  name={`Pay deduction type ${allowEmployeePayDeductionChoice ? '' : '(optional)'}`}
                  defaultValue={value || undefined}
                  onChange={onChange}
                  options={[
                    { key: 'GROSS', label: 'Gross' },
                    { key: 'NET', label: 'Net' },
                  ]}
                  width="385px"
                  data-testid="pay-deduction-type-select"
                  hasError={!!error}
                  errorMessage={
                    allowEmployeePayDeductionChoice
                      ? fieldCannotBeBlankErrorMessage
                      : undefined
                  }
                />
              );
            }}
          />
        </ContentRow>
        <ContentRow>
          <Controller
            name="salaryCalculateFrom"
            control={control}
            rules={{ required: fieldCannotBeBlankErrorMessage }}
            render={({ field, fieldState }) => {
              const { value, onChange, ref } = field;
              const { isDirty, error } = fieldState;
              return (
                <Select
                  key={value}
                  ref={ref}
                  name="Salary calculation"
                  hintText="If employee's salary is required to calculate the price, specify when to calculate it from."
                  defaultValue={value}
                  onChange={onChange}
                  options={salaryCalculationOptions}
                  hasError={!!error || !!errors?.salaryCalculateFrom}
                  errorMessage={fieldCannotBeBlankErrorMessage}
                  warningMessage={
                    isDirty ? undefined : warnings.salaryCalculateFrom
                  }
                  successMessage={
                    isDirty ? undefined : successes.salaryCalculateFrom
                  }
                  width="100%"
                  data-testid="salary-calculation-select"
                />
              );
            }}
          />
          <Controller
            name="rateTableCalculateFrom"
            control={control}
            rules={{ required: false }}
            render={({ field }) => {
              const { value, onChange, ...rest } = field;
              return (
                <Select
                  {...rest}
                  key={value?.toString()}
                  name="Rate table calculate from"
                  hintText="Logic used to control which rate table is used during the execution of a pricing formula."
                  options={rateTableCalculateFromOptions}
                  defaultValue={value?.toString()}
                  clearable
                  onChange={onChange}
                  data-testid="rate-table-calculate-from-select"
                  width="100%"
                />
              );
            }}
          />
        </ContentRow>
        <ContentRow>
          <Controller
            name="allowanceImmediateDeduction"
            control={control}
            render={({ field }) => {
              const { value, ...rest } = field;
              return (
                <Checkbox
                  {...rest}
                  label="Allowance immediate deduction"
                  showLabel
                  value={value || false}
                  disabled={isAllowanceThenSalary}
                  hintText={
                    isAllowanceThenSalary
                      ? 'Not applicable for Allowance then Salary payment source'
                      : undefined
                  }
                />
              );
            }}
          />
          <Controller
            name="includeInNmwCalculation"
            control={control}
            render={({ field, fieldState: { isDirty } }) => {
              return (
                <Checkbox
                  label="Include in NMW calculation"
                  {...field}
                  value={field.value || false}
                  successMessage={
                    isDirty ? undefined : successes.includeInNmwCalculation
                  }
                />
              );
            }}
          />
        </ContentRow>
        <Controller
          name="deductionLogic"
          control={control}
          rules={{
            required: allowanceImmediateDeduction
              ? fieldCannotBeBlankErrorMessage
              : false,
          }}
          render={({ field, fieldState }) => {
            const { value, onChange, ref, ...rest } = field;
            const { error } = fieldState;
            return (
              <Select
                {...rest}
                ref={ref}
                disabled={!allowanceImmediateDeduction}
                name="Allowance deduction logic"
                hintText="Only if allowance immediate deduction is enabled"
                value={value}
                onChange={onChange}
                options={[
                  { key: 'FULL_TERM', label: 'Full term' },
                  { key: 'UP_TO_RENEWAL_DATE', label: 'Up to renewal date' },
                  {
                    key: 'BENEFIT_FULL_TERM',
                    label: 'Full term benefit end date',
                  },
                ]}
                width="385px"
                hasError={!!error || !!errors?.['deductionLogic']}
                errorMessage={fieldCannotBeBlankErrorMessage}
                data-testid="deduction-logic-select"
              />
            );
          }}
        />
        <ContentRow>
          <Controller
            name={'showPrice'}
            control={control}
            render={({ field }) => {
              const { value, ...rest } = field;
              return (
                <Checkbox
                  {...rest}
                  label="Show pricing"
                  showLabel
                  value={value || false}
                />
              );
            }}
          />
          <Controller
            name={'showPaymentSources'}
            control={control}
            render={({ field }) => {
              const { value, ...rest } = field;
              return (
                <PayDeductionTypeWrapper>
                  <Checkbox
                    {...rest}
                    label="Show Payment Sources"
                    showLabel
                    value={value || false}
                  />
                  <Tooltip tooltipText="Show payment sources in the benefit details screen.">
                    <RIcon
                      icon={RiQuestionLine}
                      color={theme.palette.neutral[80]}
                    />
                  </Tooltip>
                </PayDeductionTypeWrapper>
              );
            }}
          />
          <Controller
            name="showTotalLoanBanner"
            control={control}
            render={({ field }) => {
              const { value, ...rest } = field;
              return (
                <PayDeductionTypeWrapper>
                  <Checkbox
                    {...rest}
                    label="Show Total Loan Banner"
                    showLabel
                    value={value || false}
                  />
                  <Tooltip tooltipText="Show total loan banner in the benefit details screen.">
                    <RIcon
                      icon={RiQuestionLine}
                      color={theme.palette.neutral[80]}
                    />
                  </Tooltip>
                </PayDeductionTypeWrapper>
              );
            }}
          />
        </ContentRow>
        <PaymentSourceWrapper $isDisabled={isPaymentSourceDisabled}>
          <PaymentSourceTitle variant={TypographyVariant.bodyLg2}>
            Payment Source
          </PaymentSourceTitle>
          <Controller
            name={`paymentSource`}
            control={control}
            render={({ field }) => {
              const { value, onChange, ref } = field;
              return (
                <PaymentSourceRow>
                  {paymentSources.map(
                    (paymentSource: {
                      label: string;
                      enum: PaymentSourceEnum;
                      tooltip: string;
                    }) => {
                      return (
                        <RadioButton
                          key={paymentSource.label}
                          ref={ref}
                          onChange={() => {
                            if (
                              paymentSource.enum ===
                              PaymentSourceEnum.ALLOWANCE_THEN_SALARY
                            ) {
                              setValue('allowanceImmediateDeduction', false);
                            }
                            onChange(paymentSource.enum);
                          }}
                          label={paymentSource.label}
                          checked={value === paymentSource.enum}
                          backgroundColor={theme.palette.neutral['00']}
                          disabled={isPaymentSourceDisabled}
                          hintText={paymentSource.tooltip}
                        />
                      );
                    }
                  )}
                </PaymentSourceRow>
              );
            }}
          />
        </PaymentSourceWrapper>
        {hasMixedPaymentSource && (
          <DefaultDeductionSourceWrapper $isDisabled={isPaymentSourceDisabled}>
            <Typography variant={TypographyVariant.bodyLg2}>
              Default deduction source
            </Typography>
            <Controller
              name="defaultDeductionSource"
              control={control}
              render={({ field }) => {
                const { value, ...rest } = field;
                const isSalaryOrAllowance =
                  paymentSource === PaymentSourceEnum.SALARY_OR_ALLOWANCE;
                const allowanceFirstHint = isSalaryOrAllowance
                  ? 'Pre-select allowance as the default payment method (default)'
                  : 'Allocate the full amount from allowance first, remainder from salary (default)';
                const salaryFirstHint = isSalaryOrAllowance
                  ? 'Pre-select salary as the default payment method'
                  : 'Allocate the full amount from salary by default, employee can adjust the split';
                return (
                  <PaymentSourceRow>
                    <RadioButton
                      {...rest}
                      onChange={() =>
                        rest.onChange(
                          DefaultDeductionSourceEnum.ALLOWANCE_FIRST
                        )
                      }
                      label="Allowance first"
                      checked={
                        value === DefaultDeductionSourceEnum.ALLOWANCE_FIRST
                      }
                      disabled={isPaymentSourceDisabled}
                      backgroundColor={theme.palette.neutral['00']}
                      hintText={allowanceFirstHint}
                    />
                    <RadioButton
                      {...rest}
                      onChange={() =>
                        rest.onChange(DefaultDeductionSourceEnum.SALARY_FIRST)
                      }
                      label="Salary first"
                      checked={
                        value === DefaultDeductionSourceEnum.SALARY_FIRST
                      }
                      disabled={isPaymentSourceDisabled}
                      backgroundColor={theme.palette.neutral['00']}
                      hintText={salaryFirstHint}
                    />
                  </PaymentSourceRow>
                );
              }}
            />
          </DefaultDeductionSourceWrapper>
        )}
        {hasMixedPaymentSource && (
          <Controller
            name="resetDeductionsAtRenewal"
            control={control}
            render={({ field }) => {
              const { value, ...rest } = field;
              return (
                <PayDeductionTypeWrapper>
                  <Checkbox
                    {...rest}
                    label="Reset deductions at renewal"
                    showLabel
                    value={value || false}
                  />
                  <Tooltip tooltipText="When enabled, employee deduction choices are reset to full salary deduction when the benefit renewal window opens. Employees are notified via the renewal email.">
                    <RIcon
                      icon={RiQuestionLine}
                      color={theme.palette.neutral[80]}
                    />
                  </Tooltip>
                </PayDeductionTypeWrapper>
              );
            }}
          />
        )}
      </PricingConfigurationWrapper>
      <PricingConfigurationWrapper key="recalculation-configuration">
        <Typography variant={TypographyVariant.h3}>
          Recalculation Config
        </Typography>
        <Controller
          name="recalculatePriceAfterEmployeeSalaryChange"
          control={control}
          render={({ field }) => {
            const { value, ...rest } = field;
            return (
              <Checkbox
                {...rest}
                label="Recalculate price after employee salary change"
                showLabel
                value={value || false}
              />
            );
          }}
        />
        <Controller
          name="recalculatePriceAfterEmployeeChange"
          control={control}
          render={({ field }) => {
            const { value, ...rest } = field;
            return (
              <Checkbox
                {...rest}
                label="Recalculate price after employee change"
                showLabel
                value={value || false}
              />
            );
          }}
        />
        <DynamicRecalculationConfig errors={errors} />
      </PricingConfigurationWrapper>
      <HeadingWrapper>
        <Typography variant={TypographyVariant.h2}>Sub prices</Typography>
        <ContentRow>
          <Button onClick={addNewSubPrice}>Add sub price</Button>
          <Button
            onClick={() => handleTestPricingFormulas()}
            disabled={isLoadingTest}
            loading={isLoadingTest}
          >
            Test pricing formulas
          </Button>
        </ContentRow>
      </HeadingWrapper>
      {fields?.length === 0 && (
        <HeadingWrapper>
          <Typography variant={TypographyVariant.bodyLg1}>
            No sub prices added yet
          </Typography>
        </HeadingWrapper>
      )}

      {fields.map((subPrice: ISubPrice, index: number) => {
        return (
          <RowWrapper key={subPrice.rank}>
            <HeadingWrapper>
              <Typography variant={TypographyVariant.h3}>
                Sub price #{index + 1}
              </Typography>
              <StyledInlineTextButton
                label="Remove"
                aria-label="Remove"
                onClick={() => remove(index)}
              />
            </HeadingWrapper>
            <ShowPricingRow>
              <Input
                name="#Rank"
                placeholder="Enter a number to order"
                defaultValue={subPrice.rank?.toString()}
                disabled={true}
                width="450px"
              />
              <Controller
                name={`additionalConfig.subPrices.${index}.name`}
                control={control}
                rules={{
                  required: fieldCannotBeBlankErrorMessage,
                }}
                render={({ field, fieldState }) => {
                  const { value, ...rest } = field;
                  const { error } = fieldState;
                  return (
                    <Input
                      key={subPrice.id + 'name'}
                      {...rest}
                      name="Sub price name"
                      value={value ?? ''}
                      hasError={!!error || !!errors?.[index]?.name}
                      errorMessage={fieldCannotBeBlankErrorMessage}
                    />
                  );
                }}
              />
              <Controller
                name={`additionalConfig.subPrices.${index}.employeeContribution`}
                control={control}
                rules={{
                  required: fieldCannotBeBlankErrorMessage,
                }}
                render={({ field, fieldState }) => {
                  const { onChange, ...rest } = field;
                  const { error } = fieldState;
                  return (
                    <Input
                      key={subPrice.id + 'employeeContribution'}
                      {...rest}
                      name="Employee contribution %"
                      hintText="This field defines what percentage of the original rate, the employee is expected to contribute."
                      hasError={
                        !!error || !!errors?.[index]?.['employeeContribution']
                      }
                      errorMessage={fieldCannotBeBlankErrorMessage}
                      onChange={(event) => {
                        onChange(event);
                        setSubPricesNeedTesting(true);
                      }}
                    />
                  );
                }}
              />
              <Controller
                name={`additionalConfig.subPrices.${index}.maxEmployerContributionAmount`}
                control={control}
                render={({ field, fieldState }) => {
                  const { onChange, ...rest } = field;
                  const { error } = fieldState;
                  return (
                    <Input
                      key={subPrice.id + 'maxEmployerContributionAmount'}
                      {...rest}
                      name="Employer contribution amount"
                      hintText="This field defines how much of the original rate, in a set amount, the employer is expected to contribute."
                      hasError={
                        !!error ||
                        !!errors?.[index]?.maxEmployerContributionAmount
                      }
                      errorMessage={fieldCannotBeBlankErrorMessage}
                      onChange={(event) => {
                        onChange(event);
                        setSubPricesNeedTesting(true);
                      }}
                    />
                  );
                }}
              />
            </ShowPricingRow>
            <ShowPricingRow>
              <AIToolWrapper
                toolName={'pricingFormula'}
                fieldName={`additionalConfig.subPrices.${index}.pricingFormula`}
                additionalValue={getValues('additionalConfig.id')}
              >
                <FormulaFieldWrapper>
                  <Controller
                    name={`additionalConfig.subPrices.${index}.pricingFormula`}
                    control={control}
                    rules={{
                      required: fieldCannotBeBlankErrorMessage,
                    }}
                    render={({ field, fieldState }) => {
                      const { value, onChange, ...rest } = field;
                      const { error } = fieldState;
                      return (
                        <Input
                          key={subPrice.id + 'pricingFormula'}
                          {...rest}
                          variant={InputVariant.TextArea}
                          name="Sub price formula"
                          value={value ?? ''}
                          hasError={
                            !!error || !!errors?.[index]?.pricing_formula
                          }
                          errorMessage={
                            errors?.[index]?.pricing_formula ??
                            fieldCannotBeBlankErrorMessage
                          }
                          width="100%"
                          height="80px"
                          onChange={(event) => {
                            onChange(event);
                            setSubPricesNeedTesting(true);
                          }}
                        />
                      );
                    }}
                  />
                  <FormulaHelpChips
                    stepsControl={stepsControl}
                    rateTableControl={rateTableControl}
                  />
                </FormulaFieldWrapper>
              </AIToolWrapper>
            </ShowPricingRow>
          </RowWrapper>
        );
      })}
    </Wrapper>
  );
};

export default memo(Prices);

const Wrapper = styled.div`
  ${({ theme }) => css`
    display: flex;
    flex-direction: column;
    padding: ${theme.spacing(3)};
    border-radius: 10px;
    border: 1px solid ${theme.palette.neutral[60]};
    gap: ${theme.spacing(2)};
  `}
`;

const RowWrapper = styled.div`
  ${({ theme }) => css`
    display: flex;
    flex-direction: column;
    border-radius: 10px;
    background-color: ${theme.palette.primary.bg};
    padding: ${theme.spacing(2)};
    gap: ${theme.spacing(3)};
  `}
`;

const PricingConfigurationWrapper = styled(RowWrapper)`
  ${({ theme }) => css`
    padding: ${theme.spacing(3)};
  `}
`;

const HeadingWrapper = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
`;

const TitleWrapper = styled.div`
  ${({ theme }) => css`
    display: flex;
    flex-direction: column;
    gap: ${theme.spacing(1)};
  `}
`;

const StyledInlineTextButton = styled(InlineTextButton)`
  ${({ theme }) => theme.typography.bodyLg2};
`;

const PaymentSourceWrapper = styled.div`
  ${({ $isDisabled }: { $isDisabled: boolean }) => css`
    opacity: ${$isDisabled ? 0.5 : 1};
  `}
`;

const PaymentSourceTitle = styled(Typography)`
  ${({ theme }) => css`
    margin-bottom: ${theme.spacing(2)};
  `}
`;

const ShowPricingRow = styled.div`
  ${({ theme }) => css`
    display: flex;
    flex-direction: row;
    align-items: baseline;
    gap: ${theme.spacing(2)};
  `}
`;

const PaymentSourceRow = styled.div`
  ${({ theme }) => css`
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
    gap: ${theme.spacing(2)};
    margin-top: ${theme.spacing(1)};
    align-items: start;

    @media (max-width: 1200px) {
      grid-template-columns: repeat(2, 1fr);
    }

    @media (max-width: 768px) {
      grid-template-columns: 1fr;
    }
  `}
`;

const ContentRow = styled.div`
  ${({ theme }) => css`
    display: flex;
    flex-direction: row;
    gap: ${theme.spacing(2)};
    align-items: center;
  `}
`;

const FormulaFieldWrapper = styled.div`
  ${({ theme }) => css`
    display: flex;
    flex-direction: column;
    gap: ${theme.spacing(2)};
    width: 100%;
  `}
`;

const PayDeductionTypeWrapper = styled.div`
  ${({ theme }) => css`
    display: flex;
    flex-direction: row;
    align-items: center;
    width: 385px;
    gap: ${theme.spacing(2)};
  `}
`;

const DefaultDeductionSourceWrapper = styled.div<{
  $isDisabled: boolean;
}>`
  ${({ theme, $isDisabled }) => css`
    margin-top: ${theme.spacing(3)};
    opacity: ${$isDisabled ? 0.5 : 1};
  `}
`;
